import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, Card, Tag, message, Spin, Empty, Modal } from 'antd';
import { QrcodeOutlined, SearchOutlined, CheckCircleOutlined, CameraOutlined, CloseOutlined } from '@ant-design/icons';
import { $api } from 'services/service';
import { ITicket, TicketStatus, getTicketStatusText, getTicketStatusColor, formatCurrency } from '../ticketModels';
import moment from 'moment';
import 'moment/locale/vi';
import TicketDetailModal from '../popup/TicketDetailModal';
import TicketUseConfirmModal from '../popup/TicketUseConfirmModal';
import { usePrintSettings } from 'contexts/PrintSettingsContext';
import browserPrinterService from 'services/browserPrinter.service';
import { Html5QrcodeScanner } from 'html5-qrcode';

interface IStudentInfo {
    id: string;
    userId: string;
    fullName: string;
    userInternalId: string;
    mobile?: string;
    profilePicUrl?: string;
    className?: string;
    [key: string]: any;
}

interface TicketUseProps {
    isPinned?: boolean;
    onSwitchToSale?: (studentInfo?: IStudentInfo) => void;
}

const TicketUse: React.FC<TicketUseProps> = ({ isPinned = false, onSwitchToSale }) => {
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const [tickets, setTickets] = useState<ITicket[]>([]);
    const [selectedTicket, setSelectedTicket] = useState<ITicket | null>(null);
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [qrModalVisible, setQrModalVisible] = useState(false);
    const [isScanning, setIsScanning] = useState(false);
    const [noTicketModalVisible, setNoTicketModalVisible] = useState(false);
    const [noTicketStudentInfo, setNoTicketStudentInfo] = useState<IStudentInfo | null>(null);
    const inputRef = useRef<any>(null);
    const scannerRef = useRef<Html5QrcodeScanner | null>(null);
    const { printSettings } = usePrintSettings();

    const handleSearch = async () => {
        if (!searchText.trim()) {
            message.warning('Vui lòng nhập mã phiếu, MSSV hoặc số điện thoại');
            return;
        }

        setLoading(true);
        try {
            const res: any = await $api.ticket.checkTicket({text: searchText.trim()});
            if (res.result) {
                const { studentInfo, tickets: ticketList } = res.result;
                if (Array.isArray(ticketList)) {
                    setTickets(ticketList);
                    if (ticketList.length === 0) {
                        // Nếu có studentInfo nhưng không có vé khả dụng
                        if (studentInfo) {
                            setNoTicketStudentInfo(studentInfo);
                            setNoTicketModalVisible(true);
                        } else {
                            message.info('Không tìm thấy phiếu nào');
                        }
                    } else {
                        setSearchText('');
                    }
                } else {
                    setTickets([]);
                    message.error('Không tìm thấy phiếu');
                }
            } else {
                setTickets([]);
                message.error('Không tìm thấy phiếu');
            }
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Có lỗi xảy ra khi tìm kiếm phiếu');
            setTickets([]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleUseTicket = (ticket: ITicket) => {
        setDetailModalVisible(false);
        setSelectedTicket(ticket);
        setConfirmModalVisible(true);
    };

    const handleViewDetail = (ticket: ITicket) => {
        setSelectedTicket(ticket);
        setDetailModalVisible(true);
    };

    const confirmUseTicket = async () => {
        if (!selectedTicket) return;

        setLoading(true);
        try {
            const res: any = await $api.ticket.useTicket({ ticketId: selectedTicket.id });
            if (res.result) {
                message.success('Sử dụng phiếu thành công!');

                // In vé tự động nếu AllowPrintTicketWhenUse = 1 VÀ type = 1 (OneTime)
                if (printSettings.AllowPrintTicketWhenUse && selectedTicket.type === 1) {
                    try {
                        message.loading({ content: 'Đang mở cửa sổ in...', key: 'printingUsed' });

                        // In vé với thông tin đã sử dụng
                        browserPrinterService.printTicket({
                            id: selectedTicket.id,
                            code: selectedTicket.code,
                            studentName: selectedTicket.studentName,
                            studentUserInternalId: selectedTicket.studentUserInternalId,
                            ticketTypeName: selectedTicket.ticketTypeName,
                            ticketTypeCode: selectedTicket.ticketTypeCode,
                            price: selectedTicket.price,
                            createdByName: selectedTicket.createdByName,
                            creationDate: selectedTicket.creationDate || new Date().toISOString(),
                            expireDate: selectedTicket.expireDate,
                            qrCode: selectedTicket.qrCode,
                            isUsed: true // Flag để biết đây là vé đã sử dụng
                        });

                        message.success({ content: 'Đã mở cửa sổ in!', key: 'printingUsed', duration: 2 });
                    } catch (printError: any) {
                        console.error('Print error:', printError);
                        message.error({
                            content: `Không thể in vé: ${printError.message || 'Lỗi không xác định'}`,
                            duration: 3
                        });
                    }
                }

                setConfirmModalVisible(false);
                setSearchText('');
                setTickets([]);
                setSelectedTicket(null);
                inputRef.current?.focus();
            }
        } catch (e: any) {
            let errMsg;
            let type = typeof e?.response?.data?.errors?.exceptionMessage;
            if (type === 'string') {
                errMsg = e?.response?.data?.errors?.exceptionMessage;
            } else if (e?.response?.data?.errors?.exceptionMessage?.title) {
                errMsg = e?.response?.data?.errors?.exceptionMessage?.title;
            } else {
                errMsg = 'Có lỗi xảy ra khi sử dụng phiếu';
            }
            message.error(errMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleTicketUpdate = (updatedTicket: ITicket) => {
        setSelectedTicket(updatedTicket);
        const updatedTickets = tickets.map(t =>
            t.id === updatedTicket.id ? updatedTicket : t
        );
        setTickets(updatedTickets);
    };

    const canUseTicket = (ticket: ITicket): boolean => {
        return ticket.status === TicketStatus.Active;
    };

    const getStatusMessage = (ticket: ITicket): string => {
        if (ticket.status === TicketStatus.Used) return 'Phiếu đã được sử dụng';
        if (ticket.status === TicketStatus.Expired) return 'Phiếu đã hết hạn';
        if (ticket.status === TicketStatus.Canceled) return 'Phiếu đã bị hủy';
        return '';
    };

    const handleScan = async (decodedText: string, decodedResult: any) => {
        // Chỉ xử lý nếu có data và chưa đang quét
        if (decodedText && !isScanning) {
            console.log('QR Code detected:', decodedText);

            // Đánh dấu đang quét để tránh quét nhiều lần
            setIsScanning(true);

            // Dừng scanner trước
            if (scannerRef.current) {
                try {
                    await scannerRef.current.clear();
                    scannerRef.current = null;
                } catch (err) {
                    console.error('Error clearing scanner:', err);
                }
            }

            // Đóng modal ngay lập tức
            setQrModalVisible(false);

            // Hiển thị loading
            setLoading(true);

            try {
                const res: any = await $api.ticket.checkTicket({text: decodedText.trim()});
                if (res.result) {
                    const { studentInfo, tickets: ticketList } = res.result;
                    if (Array.isArray(ticketList)) {
                        setTickets(ticketList);
                        if (ticketList.length === 0) {
                            // Nếu có studentInfo nhưng không có vé khả dụng
                            if (studentInfo) {
                                setNoTicketStudentInfo(studentInfo);
                                setNoTicketModalVisible(true);
                            } else {
                                message.info('Không tìm thấy phiếu nào');
                            }
                        } else {
                            message.success('Quét mã thành công!');
                        }
                    } else {
                        setTickets([]);
                        message.error('Không tìm thấy phiếu');
                    }
                } else {
                    setTickets([]);
                    message.error('Không tìm thấy phiếu');
                }
            } catch (error: any) {
                message.error(error?.response?.data?.message || 'Có lỗi xảy ra khi tìm kiếm phiếu');
                setTickets([]);
            } finally {
                setLoading(false);
                // Reset trạng thái quét sau 1 giây để có thể quét lại
                setTimeout(() => {
                    setIsScanning(false);
                }, 1000);
            }
        }
    };

    const handleError = (err: string) => {
        console.info('Camera scanning:', err);
    };

    const handleCloseQrModal = async () => {
        // Dừng và xóa scanner khi đóng modal
        if (scannerRef.current) {
            try {
                await scannerRef.current.clear();
                scannerRef.current = null;
            } catch (err) {
                console.error('Error clearing scanner:', err);
            }
        }
        setQrModalVisible(false);
        setIsScanning(false);
    };

    const handleOpenQrModal = () => {
        setQrModalVisible(true);
        setIsScanning(false);
    };

    // Effect để khởi tạo scanner khi modal mở
    useEffect(() => {
        if (qrModalVisible && !scannerRef.current) {
            // Delay một chút để đảm bảo DOM đã render
            setTimeout(() => {
                const config = {
                    fps: 10,
                    qrbox: { width: 300, height: 300 },
                    aspectRatio: 1.0
                };

                const html5QrcodeScanner = new Html5QrcodeScanner(
                    "reader",
                    config,
                    false
                );

                html5QrcodeScanner.render(handleScan, handleError);
                scannerRef.current = html5QrcodeScanner;
            }, 100);
        }

        // Cleanup khi component unmount hoặc modal đóng
        return () => {
            if (!qrModalVisible && scannerRef.current) {
                scannerRef.current.clear().catch((err) => {
                    console.error('Error clearing scanner on cleanup:', err);
                });
                scannerRef.current = null;
            }
        };
    }, [qrModalVisible]);

    return (
        <div className="space-y-4 sm:space-y-6">
            {/* Search Section */}
            <Card className="shadow-lg">
                <div className="space-y-3 sm:space-y-4">
                    <h2 className="text-lg sm:text-2xl font-bold text-gray-700 flex items-center gap-2">
                        <QrcodeOutlined className="text-blue-500 text-base sm:text-xl" />
                        <span className="hidden xs:inline">Quét mã hoặc nhập thông tin</span>
                        <span className="xs:hidden">Tìm phiếu</span>
                    </h2>

                    <div className="flex flex-col xs:flex-row gap-2 sm:gap-3">
                        <Input
                            ref={inputRef}
                            size="large"
                            placeholder="Mã phiếu, MSSV hoặc SĐT..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyPress={handleKeyPress}
                            prefix={<SearchOutlined className="text-gray-400" />}
                            className="text-sm sm:text-lg flex-1"
                            autoFocus
                            allowClear
                        />
                        <div className="flex gap-2">
                            <Button
                                type="default"
                                size="large"
                                onClick={() => setQrModalVisible(true)}
                                icon={<CameraOutlined />}
                                className="flex-1 xs:flex-none px-3 sm:px-6 h-10 sm:h-12 text-sm sm:text-lg font-semibold"
                                style={{ borderColor: '#1890ff', color: '#1890ff' }}
                            >
                                <span className="hidden sm:inline">Quét QR</span>
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                onClick={handleSearch}
                                loading={loading}
                                icon={<SearchOutlined />}
                                className="flex-1 xs:flex-none px-3 sm:px-8 h-10 sm:h-12 text-sm sm:text-lg font-semibold focus:outline-none focus:shadow-none active:shadow-none"
                            >
                                <span className="hidden sm:inline">Tìm kiếm</span>
                            </Button>
                        </div>
                    </div>

                    <p className="text-gray-500 text-xs sm:text-sm hidden sm:block">
                        💡 Mẹo: Nhấn "Quét QR" để mở camera, hoặc nhập thông tin rồi nhấn Enter để tìm kiếm nhanh
                    </p>
                </div>
            </Card>

            {/* Results Section */}
            {loading ? (
                <div className="flex justify-center items-center py-10 sm:py-20">
                    <Spin size="large" />
                </div>
            ) : tickets.length > 0 ? (
                <div className={`grid gap-3 sm:gap-4 ${isPinned ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                    {tickets.map((ticket) => {
                        const isValid = canUseTicket(ticket);
                        return (
                            <div
                                key={ticket.id}
                                className={`relative bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden ${
                                    isValid ? 'border-2 border-green-500' : 'border-2 border-gray-400 opacity-80'
                                }`}
                                style={{
                                    background: isValid
                                        ? 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)'
                                        : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                                    minWidth: '260px'
                                }}
                            >
                                {/* Decorative Notches - Ticket Style - Hidden on very small screens */}
                                <div className="absolute top-1/2 -left-3 w-5 h-5 sm:w-6 sm:h-6 bg-gray-100 rounded-full transform -translate-y-1/2 shadow-inner hidden xs:block"></div>
                                <div className="absolute top-1/2 -right-3 w-5 h-5 sm:w-6 sm:h-6 bg-gray-100 rounded-full transform -translate-y-1/2 shadow-inner hidden xs:block"></div>

                                {/* Top Section */}
                                <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-2">
                                    {/* Owner Info - Top Left Corner */}
                                    <div className="absolute top-2 left-3 sm:left-4 text-left max-w-[45%]">
                                        <p className="text-[10px] sm:text-xs text-gray-500 font-medium truncate">{ticket.studentName}</p>
                                        <p className="text-[10px] sm:text-xs text-gray-400 truncate"> {ticket.studentUserInternalId}</p>
                                    </div>

                                    {/* Expire Date - Top Right Corner */}
                                    <div className="absolute top-2 right-3 sm:right-4 text-right">
                                        <p className="text-[10px] sm:text-xs text-gray-500">HSD</p>
                                        <p className={`text-[10px] sm:text-xs font-bold ${moment(ticket.expireDate).isBefore(moment()) ? 'text-red-600' : 'text-green-600'}`}>
                                            {moment(ticket.expireDate).format('DD/MM/YYYY')}
                                        </p>
                                    </div>

                                    {/* Main Content - Center */}
                                    <div className="mt-8 sm:mt-10 mb-3 sm:mb-4 text-center">
                                        {/* Ticket Code - Very Prominent */}
                                        <div className="mb-2">
                                            <p className="font-mono font-extrabold text-xl sm:text-3xl text-blue-600 tracking-wider">
                                                {ticket.code}
                                            </p>
                                        </div>

                                        {/* Ticket Type Name - Prominent */}
                                        <div className="mb-2 sm:mb-3">
                                            <div className="inline-block bg-blue-500 text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full shadow-md">
                                                <p className="font-bold text-sm sm:text-base">{ticket.ticketTypeName}</p>
                                            </div>
                                        </div>

                                        {/* Status - Very Prominent */}
                                        <div className="">
                                            <Tag
                                                color={getTicketStatusColor(ticket.status)}
                                                className="text-base sm:text-xl font-extrabold px-4 sm:px-8 py-1.5 sm:py-2 rounded-lg shadow-lg border-2"
                                                style={{
                                                    fontSize: window.innerWidth < 375 ? '14px' : '18px',
                                                    lineHeight: '1.5'
                                                }}
                                            >
                                                {getTicketStatusText(ticket.status)}
                                            </Tag>
                                        </div>
                                    </div>
                                </div>

                                {/* Warning Message */}
                                {!isValid && (
                                    <div className="px-4 sm:px-6 pb-2">
                                        <div className="bg-red-100 border-l-4 border-red-500 rounded p-1.5 sm:p-2 text-red-700 text-center font-semibold text-xs sm:text-sm">
                                            ⚠️ {getStatusMessage(ticket)}
                                        </div>
                                    </div>
                                )}

                                {/* Bottom Section - Action Buttons */}
                                <div className="px-4 sm:px-6 pb-3 sm:pb-4">
                                    <div className="flex gap-2 sm:gap-3">
                                        <Button
                                            type="primary"
                                            size="large"
                                            block
                                            disabled={!isValid}
                                            onClick={() => handleUseTicket(ticket)}
                                            icon={<CheckCircleOutlined />}
                                            className="font-bold text-sm sm:text-base h-10 sm:h-12 shadow-md"
                                            style={{
                                                background: isValid ? '#10b981' : undefined,
                                                borderColor: isValid ? '#10b981' : undefined
                                            }}
                                        >
                                            Sử dụng
                                        </Button>
                                        <Button
                                            size="large"
                                            onClick={() => handleViewDetail(ticket)}
                                            className="text-sm sm:text-base h-10 sm:h-12 min-w-[80px] sm:min-w-[110px] font-semibold shadow-md"
                                        >
                                            Chi tiết
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : searchText && !loading ? (
                <Empty description="Không tìm thấy phiếu nào" />
            ) : null}

            {/* Use Confirm Modal */}
            <TicketUseConfirmModal
                visible={confirmModalVisible}
                ticket={selectedTicket}
                loading={loading}
                onConfirm={confirmUseTicket}
                onCancel={() => setConfirmModalVisible(false)}
            />

            {/* Detail Modal */}
            <TicketDetailModal
                visible={detailModalVisible}
                ticket={selectedTicket}
                onClose={() => setDetailModalVisible(false)}
                onTicketUpdate={handleTicketUpdate}
                showUseButton={true}
                onUseTicket={handleUseTicket}
            />

            {/* QR Code Scanner Modal */}
            <Modal
                open={qrModalVisible}
                onCancel={() => setQrModalVisible(false)}
                footer={null}
                width="90%"
                centered
                styles={{ body: { padding: '20px' } }}
                style={{ maxWidth: '600px' }}
            >
                <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
                        <CameraOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                        Quét mã QR
                    </h3>
                    <p style={{ color: '#666', fontSize: '14px' }}>
                        Đưa mã QR vào khung hình để quét
                    </p>
                </div>

                <div style={{
                    width: '100%',
                    maxWidth: '500px',
                    margin: '0 auto',
                    border: '2px solid #1890ff',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    position: 'relative',
                    paddingTop: '100%'
                }}>
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                    }}>
                        <div id="reader" style={{ width: '100%', height: '100%' }}></div>
                    </div>
                </div>

                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                    <Button
                        onClick={() => setQrModalVisible(false)}
                        size="large"
                        icon={<CloseOutlined />}
                        block
                    >
                        Đóng
                    </Button>
                </div>
            </Modal>

            {/* No Ticket Available Modal */}
            <Modal
                open={noTicketModalVisible}
                onCancel={() => setNoTicketModalVisible(false)}
                footer={null}
                centered
                width={400}
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{
                        fontSize: '48px',
                        marginBottom: '16px',
                        color: '#faad14'
                    }}>
                        ⚠️
                    </div>
<h3 style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        marginBottom: '16px',
                        color: '#333'
                    }}>
                        Không có vé khả dụng
                    </h3>
                    {noTicketStudentInfo && (
                        <div style={{
                            marginBottom: '24px',
                            padding: '12px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px'
                        }}>
<p style={{
                                fontSize: '16px',
                                fontWeight: 500,
                                marginBottom: '4px',
                                color: '#1890ff'
                            }}>
                                {noTicketStudentInfo.fullName}
                            </p>
                            <p style={{
                                fontSize: '14px',
                                color: '#666'
                            }}>
                                MSSV: {noTicketStudentInfo.userInternalId}
                            </p>
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                        <Button
                            size="large"
                            onClick={() => {
                                setNoTicketModalVisible(false);
                                if (onSwitchToSale && noTicketStudentInfo) {
                                    onSwitchToSale(noTicketStudentInfo);
                                }
                                setNoTicketStudentInfo(null);
                            }}
                            style={{ minWidth: '120px' }}
                        >
                            Đến mua
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => {
                                setNoTicketModalVisible(false);
                                setNoTicketStudentInfo(null);
                            }}
                            style={{ minWidth: '120px' }}
                        >
                            Đóng
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default TicketUse;
